import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Container, Header, Segment, Grid, Card, Message, Divider, List, Icon } from 'semantic-ui-react';
import '../Playbook.css';

class ProposalProcessExplained extends Component {
    render() {
        return (
            <Container fluid className="playbook-container">
                <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                    <i aria-hidden="true" className="icon">
                        <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                    </i>
                    Closing
                </h1>
                <div className="ui divider"></div>

                <Grid stackable>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Segment className="playbook-content">
                                <Header as="h2" className="section-header">Why Proposals Matter</Header>
                                
                                <p>
                                    A well-written proposal is among the most effective sales tools one can deploy. 
                                    <strong> It is how you close.</strong> The reason why—buying decisions are most often 
                                    made when the seller is NOT in the room. Therefore, to give the seller the best chance 
                                    to succeed, he/she must equip his/her key stakeholder with enough information to 
                                    "carry the message properly." That's where the proposal comes in.
                                </p>

                                <Message info>
                                    <Message.Header>The Critical Moment</Message.Header>
                                    <p>
                                        When you think of a proposal and its value, envision the point in a sales cycle where 
                                        the seller has done his/her job. The sales meetings are complete, the product or service 
                                        has been pitched, pricing and timelines have been discussed, and now the prospect needs 
                                        to make a decision.
                                    </p>
                                </Message>

                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>The Decision Meeting Reality</Card.Header>
                                        <Card.Description>
                                            <p>
                                                The prospect will likely hold a meeting with influencers and decision makers, 
                                                some of which have not been in any of the sales meetings. This is where the 
                                                decision to buy will occur. Therefore, the seller has two choices:
                                            </p>
                                            
                                            <Grid stackable columns={2} style={{marginTop: '10px', marginBottom: '10px'}}>
                                                <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                                    <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                                        <Card fluid color="red" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                            <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                                <Card.Header>
                                                                    <Icon name="warning sign" />
                                                                    Option 1: Outsource Your Message
                                                                </Card.Header>
                                                                <Card.Description style={{flex: 1}}>
                                                                    Outsource the seller's message and hope the key stakeholder 
                                                                    represents the seller the right way internally.
                                                                    
                                                                    <p><strong>Result:</strong> Creates confusion and lost opportunities.</p>
                                                                </Card.Description>
                                                            </Card.Content>
                                                        </Card>
                                                    </Grid.Column>
                                                    
                                                    <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                                        <Card fluid color="green" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                            <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                                <Card.Header>
                                                                    <Icon name="checkmark" />
                                                                    Option 2: Equip Your Stakeholder
                                                                </Card.Header>
                                                                <Card.Description style={{flex: 1}}>
                                                                    Equip the key stakeholder to represent the company, the solution, 
                                                                    and pricing with the seller's input.
                                                                    
                                                                    <p><strong>Result:</strong> Creates success and winning outcomes.</p>
                                                                </Card.Description>
                                                            </Card.Content>
                                                        </Card>
                                                    </Grid.Column>
                                                </Grid.Row>
                                            </Grid>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Message success>
                                    <Message.Header>The Meeting Outcome</Message.Header>
                                    <p>
                                        This meeting goes one of two ways. Either the stakeholder attempts to describe the 
                                        product or solution that the seller has described, or the stakeholder provides the 
                                        meeting participants with a well-written and to-the-point proposal (provided by the seller) 
                                        that represents and "speaks" for the seller when he/she is not invited to the room.
                                    </p>
                                    <p><strong>One method creates confusion. One method creates success.</strong></p>
                                </Message>

                                <Divider />

                                <Header as="h2" className="section-header">Proposal Scope and Success</Header>
                                
                                <p>
                                    In this module, we dive into what creates a successful proposal. Then we help your team 
                                    create one, and guide you in the process of repetition. And just in case you are thinking 
                                    "our sales process is too complex for a simple proposal," this process and these templates 
                                    have been successfully used to sell:
                                </p>

                                <Grid stackable columns={2} style={{marginTop: '10px', marginBottom: '10px'}}>
                                    <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>Complex Solutions</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <List bulleted>
                                                            <List.Item>Multi-million-dollar complex IT solutions to enterprise corporations</List.Item>
                                                            <List.Item>SaaS solutions to startups</List.Item>
                                                            <List.Item>Services to companies big and small</List.Item>
                                                            <List.Item>Everything in between</List.Item>
                                                        </List>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Message warning style={{height: '100%', display: 'flex', flexDirection: 'column', flex: 1}}>
                                                <Message.Header>The Key Principle</Message.Header>
                                                <div style={{flex: 1}}>
                                                    <p>
                                                        Once you get to the point of buying, you must be able to create a document 
                                                        that <strong>simplifies the complex</strong>.
                                                    </p>
                                                    <p>We tell you how to do that...and we help you do it.</p>
                                                </div>
                                            </Message>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Divider />

                                <Header as="h2" className="section-header">What the Proposal is NOT</Header>
                                
                                <p>
                                    Let's first start with what the proposal is NOT. This may make things a little simpler 
                                    as you think through it:
                                </p>

                                <Grid stackable columns={2} style={{marginTop: '10px', marginBottom: '10px'}}>
                                    <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>
                                                        <Icon name="x" color="red" />
                                                        NOT Sales Material
                                                    </Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        The proposal is not your sales material. You have already provided that. 
                                                        The proposal is the executive summary of the sales material combined with 
                                                        how the product/service fits into the buyer's organization. It also includes 
                                                        pricing and scheduling.
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>

                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>
                                                        <Icon name="x" color="red" />
                                                        NOT a Marketing Slick
                                                    </Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        You will see in our examples that we have a couple images representing 
                                                        the seller and the prospect, but this isn't a glossy document. This is 
                                                        an engagement-specific document that is two (maybe three) pages long 
                                                        and that gets to the important points.
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>

                                    <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>
                                                        <Icon name="x" color="red" />
                                                        NOT a Detailed Project Plan
                                                    </Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        It describes the activities and/or functions of a product, service, or solution. 
                                                        It is not a product specification, a requirements document, or a task-based 
                                                        project plan. If those have been developed by the selling team, then the 
                                                        proposal may reference those documents.
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>

                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>
                                                        <Icon name="x" color="red" />
                                                        NOT Marketing Boilerplate
                                                    </Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        It is not a long document filled with boilerplate information built by 
                                                        the marketing team. It's a client- and opportunity-specific document 
                                                        built by the seller.
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>

                                    <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>
                                                        <Icon name="x" color="red" />
                                                        NOT a Legal Document
                                                    </Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        It's not binding, there is no signature blank, and it doesn't contain 
                                                        a bunch of legal language. That's the contract. When done right, the 
                                                        proposal becomes the basis of the contract.
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>

                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>
                                                        <Icon name="x" color="red" />
                                                        NOT for Knowledgeable Audiences
                                                    </Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        The proposal is not a document that assumes the audience knows the seller 
                                                        or the problem being solved. It is actually designed as an "executive summary" 
                                                        for an audience that does NOT know the seller or problem being solved.
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Divider />

                                <Header as="h2" className="section-header">What IS the Proposal</Header>
                                
                                <p>So now let's look at what a proposal is. Don't overthink this.</p>

                                <Message info>
                                    <Message.Header>The Three Primary Sections</Message.Header>
                                    <List ordered>
                                        <List.Item><strong>Introduction</strong></List.Item>
                                        <List.Item><strong>Body</strong> (description of solution)</List.Item>
                                        <List.Item><strong>Pricing and Schedule</strong></List.Item>
                                        <List.Item><strong>Assumptions</strong></List.Item>
                                    </List>
                                </Message>

                                <Grid stackable columns={2} style={{marginTop: '10px', marginBottom: '10px'}}>
                                    <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid color="blue" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>Introduction Section</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p><strong>Primary Purpose:</strong> Orient the reader into the sales cycle.</p>
                                                        
                                                        <p>The introduction should include:</p>
                                                        <List bulleted>
                                                            <List.Item>A brief description of the buying company and the problem being solved</List.Item>
                                                            <List.Item>A brief description of the seller and the product/services it provides</List.Item>
                                                            <List.Item>A brief sentence or two describing the purpose of the proposal</List.Item>
                                                        </List>
                                                        
                                                        <p>
                                                            The purpose is such that as new decision maker(s) or influencer(s) 
                                                            enter the sales cycle, they can quickly come up to speed on the seller 
                                                            and the problem being solved.
                                                        </p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid color="green" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>Body Section</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p><strong>Primary Content:</strong> Describe complex solution in simple terms.</p>
                                                        
                                                        <p>This is not supposed to be:</p>
                                                        <List bulleted>
                                                            <List.Item>A detailed dissertation</List.Item>
                                                            <List.Item>A project plan</List.Item>
                                                            <List.Item>Other detailed document</List.Item>
                                                        </List>
                                                        
                                                        <p>
                                                            This is a few paragraphs that describe the solution being sold. 
                                                            When building this section, the seller should think through high-level 
                                                            the activities that must occur to implement a product or execute a service.
                                                        </p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                    
                                    <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid color="orange" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>Pricing and Schedule</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p><strong>Easiest Portion:</strong> Clear financial and timeline information.</p>
                                                        
                                                        <p>When a prospect reads this section, they should be able to see:</p>
                                                        <List bulleted>
                                                            <List.Item>Exactly how they will be charged</List.Item>
                                                            <List.Item>The schedule they will be charged on</List.Item>
                                                            <List.Item>The duration of the engagement</List.Item>
                                                            <List.Item>What they will be getting with the engagement</List.Item>
                                                        </List>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                        
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Card fluid color="purple" style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                                                <Card.Content style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                                                    <Card.Header>Assumptions</Card.Header>
                                                    <Card.Description style={{flex: 1}}>
                                                        <p><strong>Optional Section:</strong> For smaller level things that may affect the project.</p>
                                                        
                                                        <p>
                                                            If you, as the seller, are making large assumptions that affect 
                                                            the success of the solution, then those should be mentioned in the body. 
                                                            However, there could be smaller assumptions that you are making. 
                                                            Those would go as bulleted items in this section.
                                                        </p>
                                                        
                                                        <p>
                                                            Remember this isn't a contract and is still part of the sales process. 
                                                            Therefore, you don't want to use this section to list all the terms 
                                                            and conditions that normally fit into a contract.
                                                        </p>
                                                    </Card.Description>
                                                </Card.Content>
                                            </Card>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Divider />

                                <Header as="h2" className="section-header">Why a Proposal</Header>
                                
                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>The Professional Advantage</Card.Header>
                                        <Card.Description>
                                            <p>
                                                Quite often, this is the first time you as a seller get to demonstrate 
                                                your professionalism and capabilities. When a prospect sees a professional 
                                                proposal built to the opportunity, it increases the chances for success.
                                            </p>
                                            
                                            <p>When done right, it:</p>
                                            <List bulleted>
                                                <List.Item>Creates the expectations for the delivery (in whatever form that takes)</List.Item>
                                                <List.Item>The body, pricing and schedule become the basis of the contract</List.Item>
                                                <List.Item>Demonstrates professionalism</List.Item>
                                                <List.Item>Sets the boundaries for success</List.Item>
                                                <List.Item>Can be used as a valuable tool to qualify prospects externally</List.Item>
                                                <List.Item>Helps qualify opportunities in the pipeline internally</List.Item>
                                            </List>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Grid stackable columns={2} style={{marginTop: '10px', marginBottom: '10px'}}>
                                    <Grid.Row style={{display: 'flex', alignItems: 'stretch'}}>
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Message info style={{height: '100%', display: 'flex', flexDirection: 'column', flex: 1}}>
                                                <Message.Header>Internal Pipeline Management</Message.Header>
                                                <div style={{flex: 1}}>
                                                    <p>
                                                        If you as a company leader or sales manager are reviewing your pipeline 
                                                        in CRM and/or speaking with your sales team about their activity, it is 
                                                        very easy to use subjective information to determine the phase of the sales cycle.
                                                    </p>
                                                    
                                                    <p>We help companies see the value in fleshing out sales data with the simple question:</p>
                                                    <p><strong>"Does the prospect have a proposal?"</strong></p>
                                                </div>
                                            </Message>
                                        </Grid.Column>
                                        
                                        <Grid.Column style={{display: 'flex', padding: '5px'}}>
                                            <Message warning style={{height: '100%', display: 'flex', flexDirection: 'column', flex: 1}}>
                                                <Message.Header>Objective vs. Subjective Information</Message.Header>
                                                <div style={{flex: 1}}>
                                                    <p>
                                                        If that answer is "yes," then we can review the proposal and VERIFY that 
                                                        the salesperson has described the problem, described the solution, and 
                                                        provided pricing and scheduling information.
                                                    </p>
                                                    
                                                    <p>
                                                        If the answer is "no" but the salesperson says "we have discussed all these 
                                                        things and they are interested," then all of that may be true—but it's not 
                                                        necessarily VERIFIABLE.
                                                    </p>
                                                </div>
                                            </Message>
                                        </Grid.Column>
                                    </Grid.Row>
                                </Grid>

                                <Divider />

                                <Header as="h2" className="section-header">Qualifying Prospects Externally</Header>
                                
                                <Card fluid>
                                    <Card.Content>
                                        <Card.Header>The Proposal Test</Card.Header>
                                        <Card.Description>
                                            <p>
                                                Not only does it assist with creating better information internally, it also 
                                                assists with qualifying out a potential prospect. If we are selling and we have 
                                                a prospect that demonstrates interest and we have discussed solution-based details, 
                                                then we can ask:
                                            </p>
                                            
                                            <Message>
                                                <p><strong>"Would you like for me to put a proposal together of what a project would look like?"</strong></p>
                                            </Message>
                                            
                                            <p>
                                                This demonstrates our willingness to do work on behalf of the prospect. 
                                                If the prospect is interested but not a buyer, then the prospect will likely 
                                                say "no" to the offer. There are a couple of reasons for this:
                                            </p>
                                            
                                            <List bulleted>
                                                <List.Item>They don't want the seller to waste time</List.Item>
                                                <List.Item>Having a proposal in hand can put pressure on the prospect</List.Item>
                                                <List.Item>They can potentially feel some obligation to circulate and/or feel an obligation to give you a "yes" or "no"</List.Item>
                                            </List>
                                            
                                            <p>
                                                If a prospect does want a proposal, then it gives the seller all the "rights" 
                                                to follow up until the seller gets the definitive answer we all are looking for.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                                <Message success>
                                    <Message.Header>Multiply Teaser</Message.Header>
                                    <p>
                                        Here at Multiply, we have led organizations where we didn't manage our producers 
                                        to numbers of meetings but we did manage our project managers to produce at least 
                                        one "unsolicited" proposal per month. In other words, we provided them with the 
                                        template and told them to fill it out based on the problems they saw within a 
                                        client organization they were already working in. This created leads with known 
                                        solutions for the sales team to "sell."
                                    </p>
                                </Message>

                                <Divider />

                                <Header as="h2" className="section-header">Conclusion</Header>
                                
                                <Card fluid className="highlight-card">
                                    <Card.Content>
                                        <Card.Header>The Power of the Process</Card.Header>
                                        <Card.Description>
                                            <p>
                                                The proposal process is powerful. There is no downside. The concepts are simple, 
                                                but they are not natural. The initial proposals can take time, but it will quickly 
                                                become routine. The content begins to repeat itself, the thought process becomes 
                                                natural, and the document template is built. Sales absolutely follow.
                                            </p>
                                            
                                            <p>
                                                As salespeople, we never want to "outsource our message" to someone else. 
                                                And if decisions are made when we are not in the room, then we have to "speak" 
                                                when we are not there.
                                            </p>
                                        </Card.Description>
                                    </Card.Content>
                                </Card>

                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Container>
        );
    }
}

export default observer(ProposalProcessExplained);